import { type Location, type PropertyFiltersType } from '@aikoa/common'
import { Trans, useTranslations } from '@aikoa/i18n/react'
import { useCallback, useState } from 'react'
import { View } from 'react-native'
import Animated, { FadeIn } from 'react-native-reanimated'
import { type FeedbackFlowData } from '~/components/feedback/feedbackFlowTypes'
import FlowNavigation from '~/components/FlowNavigation'
import { type FlowData } from '~/components/flows/FlowProvider'
import { useFlowManager } from '~/components/flows/useFlowManager'
import { InfoModal } from '~/components/InfoModal'
import { ConfigureFeedbackInterest } from '~/components/interests/ConfigureFeedbackInterest'
import { SectionTitle } from '~/components/layout/Sections'
import { Body } from '~/components/typography/Content'
import { Input } from '~/components/ui/input'
import { Text } from '~/components/ui/text'
import { cn } from '~/lib/utils'

export function PropertyInterestQuestion({
  interestData,
  feedbackAnswers,
  updateData,
}: {
  interestData: FeedbackFlowData['propertyInterest']
  feedbackAnswers?: FeedbackFlowData['feedbackAnswers']
  updateData: <K extends keyof FeedbackFlowData['propertyInterest']>(
    data: FlowData[K] | ((current: FlowData[K]) => FlowData[K]) | undefined
  ) => void
}) {
  const { t } = useTranslations('feedback')

  const handleFiltersChange = useCallback(
    (
      nextFilters: PropertyFiltersType | ((current: PropertyFiltersType) => PropertyFiltersType)
    ) => {
      updateData((current) => {
        const currentInterest = (current as Partial<FeedbackFlowData>['propertyInterest']) ?? {
          filters: {},
        }

        const currentFilters = currentInterest.filters as PropertyFiltersType | undefined

        return {
          ...currentInterest,
          filters:
            typeof nextFilters === 'function' ? nextFilters(currentFilters ?? {}) : nextFilters,
        }
      })
    },
    [updateData]
  )

  const handleLocationsChange = useCallback(
    (locations: Location[] | ((current: Location[]) => Location[])) => {
      updateData((current) => {
        const currentInterest = (current as Partial<FeedbackFlowData>['propertyInterest']) ?? {
          filters: {},
          locations: [],
        }

        const currentLocations = currentInterest.locations as Location[] | undefined

        return {
          ...currentInterest,
          locations:
            typeof locations === 'function' ? locations(currentLocations ?? []) : locations,
        }
      })
    },
    [updateData]
  )

  return (
    <View>
      <View className="mb-6 gap-4">
        <Body className="text-pretty text-center font-light text-lg/normal text-foreground">
          {t(($) => $.interest.questionPrompt)}
        </Body>
        <InterestHowItWorksModal />
      </View>
      <ConfigureFeedbackInterest
        interestData={interestData}
        onChangeFilters={handleFiltersChange}
        onChangeLocations={handleLocationsChange}
      />
    </View>
  )
}

export function HowItWorksBullets() {
  const { t } = useTranslations('feedback')

  return (
    <View className="gap-3 px-1">
      <View className="flex-row items-start gap-3">
        <View className="mt-2 h-1.5 w-1.5 rounded-full bg-neutral-500" />
        <Body className="flex-1 text-pretty text-base/relaxed">
          <Text className="font-medium">{t(($) => $.interest.bullet1)}</Text>{' '}
          {t(($) => $.interest.bullet1Description)}
        </Body>
      </View>

      <View className="flex-row items-start gap-3">
        <View className="mt-2 h-1.5 w-1.5 rounded-full bg-neutral-500" />
        <Body className="flex-1 text-pretty text-base/relaxed">
          <Text className="font-medium">{t(($) => $.interest.bullet2)}</Text>{' '}
          {t(($) => $.interest.bullet2Description)}
        </Body>
      </View>

      <View className="flex-row items-start gap-3">
        <View className="mt-2 h-1.5 w-1.5 rounded-full bg-neutral-500" />
        <Body className="flex-1 text-pretty text-base/relaxed">
          <Text className="font-medium">{t(($) => $.interest.bullet3)}</Text>{' '}
          {t(($) => $.interest.bullet3Description)}
        </Body>
      </View>

      <View className="flex-row items-start gap-3">
        <View className="mt-2 h-1.5 w-1.5 rounded-full bg-neutral-500" />
        <Body className="flex-1 text-pretty text-base/relaxed">
          <Text className="font-medium">{t(($) => $.interest.bullet4)}</Text>
          {t(($) => $.interest.bullet4Description)}
        </Body>
      </View>
    </View>
  )
}

function InterestHowItWorksModal() {
  const { t } = useTranslations('feedback')

  return (
    <InfoModal
      triggerLabel={t(($) => $.interest.howItWorks)}
      triggerVariant="outline"
      triggerSize="sm"
      className="w-full max-w-md gap-8 pt-6">
      <View className="gap-5">
        <SectionTitle className="text-center">{t(($) => $.interest.howItWorks)}</SectionTitle>

        <Body className="text-pretty text-center text-base/relaxed">
          <Trans
            t={t}
            i18nKey={($) => $.interest.howItWorksDescription}
            components={{
              strong: <Text className="font-medium text-inherit" />,
            }}
          />
        </Body>

        <HowItWorksBullets />
      </View>
    </InfoModal>
  )
}

const minimumDescriptionLength = 10
const maximumDescriptionLength = 500

export function PropertyInterestDescription({
  flowData,
  onUpdateDescription,
  onNext,
  onPrevious,
  children,
}: {
  flowData: Partial<FeedbackFlowData>
  onUpdateDescription: (description: string) => void
  onNext?: () => void
  onPrevious?: () => void
  children?: React.ReactNode
}) {
  const { t } = useTranslations('feedback')
  const flowManager = useFlowManager()
  const nextPhase = onNext ?? flowManager.nextPhase
  const previousPhase = onPrevious ?? flowManager.previousPhase

  const description = flowData.interestDescription?.description ?? ''
  const descriptionLength = description.length

  const isValid =
    !!description &&
    descriptionLength >= minimumDescriptionLength &&
    descriptionLength <= maximumDescriptionLength

  const placeholder = t(($) => $.interest.descriptionPlaceholder)

  const [showPlaceholder, setShowPlaceholder] = useState(false)

  return (
    <View className="gap-6">
      <Body className="text-pretty text-center font-light text-lg/normal text-foreground">
        {t(($) => $.interest.descriptionPrompt)}
      </Body>
      <View className="max-w-lg gap-2">
        <Input
          testID="interest-description-input"
          value={description}
          placeholder={placeholder}
          onChangeText={onUpdateDescription}
          multiline
          numberOfLines={5}
          variant="box"
          maxLength={maximumDescriptionLength}
          onFocus={() => setShowPlaceholder(true)}
          onBlur={() => setShowPlaceholder(false)}
        />
        <View className="relative mt-2 w-full gap-3 px-3 pb-12">
          <View className="w-full flex-row items-center justify-between">
            <Text className={cn('text-sm text-secondary', !isValid && 'text-destructive/80')}>
              {t(($) => $.interest.characters)}
            </Text>
            <Text
              className={cn(
                'text-right text-sm text-secondary',
                !isValid && 'text-destructive/80'
              )}>
              {`${descriptionLength} / ${maximumDescriptionLength}`}
            </Text>
          </View>
          {showPlaceholder && (
            <Animated.Text
              entering={FadeIn.duration(150)}
              className={cn(
                'absolute bottom-0 left-0 text-pretty px-3 font-normal text-secondary'
              )}>
              {placeholder}
            </Animated.Text>
          )}
        </View>
        {children}
      </View>
      <FlowNavigation
        nextTestId="interest-flow-next-button"
        previousTestId="interest-flow-previous-button"
        className="web:relative web:py-0 web:sm:py-0 mt-12 h-auto px-0 py-0"
        onNext={() => nextPhase()}
        onPrevious={() => previousPhase()}
        nextLabel={t(($) => $.flow.next)}
        previousLabel={t(($) => $.flow.back)}
        nextDisabled={!isValid}
        showBackground={false}
      />
    </View>
  )
}
